import { useState } from "react";

import {
  Banner,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Label,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  toast,
} from "@zentrack/ui";

import { useWebApp } from "../app/app-provider";
import { StorageHealth } from "../app/storage-health";
import { BackupPanel } from "../components/backup-panel";
import { PwaInstallPanel } from "../components/pwa-install-panel";

const IDLE_THRESHOLD_OPTIONS = [
  { value: "1", label: "1 minute" },
  { value: "2", label: "2 minutes" },
  { value: "5", label: "5 minutes" },
  { value: "10", label: "10 minutes" },
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
];

export function SettingsRoute() {
  const { api, perform, snapshot } = useWebApp();
  const settings = snapshot?.settings;

  const [saving, setSaving] = useState(false);

  if (!settings) {
    return null;
  }

  async function handleAutoPauseToggle(checked: boolean) {
    setSaving(true);
    try {
      await perform(() => api.settings.update({ autoPauseOnIdle: checked }));
      toast(checked ? "Auto-pause enabled" : "Auto-pause disabled");
    } finally {
      setSaving(false);
    }
  }

  async function handleThresholdChange(value: string) {
    const minutes = Number(value);
    if (!Number.isInteger(minutes) || minutes < 1) return;
    setSaving(true);
    try {
      await perform(() => api.settings.update({ idleThresholdMinutes: minutes }));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader description="Configure session, storage, and install behavior." title="Settings" />

      <Banner
        tone="info"
        title="Browser edition"
        message="Foreground-app tracking, installed-app discovery, and native idle detection are only available in the ZenTrack desktop app. The browser app tracks timer time and your manual entries locally."
      />

      <Card>
        <CardHeader>
          <CardTitle>Idle detection</CardTitle>
          <CardDescription>
            Automatically pause the timer when no activity is detected for the chosen duration. In
            the browser, idle is measured from page interactions only — it cannot detect OS-wide
            inactivity while ZenTrack is in the background.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <Field className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <FieldLabel htmlFor="auto-pause-toggle">Auto-pause on idle</FieldLabel>
              <FieldDescription>
                {settings.autoPauseOnIdle
                  ? "Enabled — timer will pause when idle"
                  : "Disabled"}
              </FieldDescription>
            </div>
            <Switch
              checked={settings.autoPauseOnIdle}
              disabled={saving}
              id="auto-pause-toggle"
              onCheckedChange={handleAutoPauseToggle}
            />
          </Field>

          {settings.autoPauseOnIdle ? (
            <Field className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-1">
                <Label htmlFor="idle-threshold-select">Idle threshold</Label>
                <FieldDescription>Pause after this much inactivity.</FieldDescription>
              </div>
              <Select
                disabled={saving}
                items={IDLE_THRESHOLD_OPTIONS}
                value={String(settings.idleThresholdMinutes)}
                onValueChange={(value) => {
                  if (value !== null) void handleThresholdChange(value);
                }}
              >
                <SelectTrigger className="w-36" id="idle-threshold-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IDLE_THRESHOLD_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          ) : null}
        </CardContent>
      </Card>

      <StorageHealth />

      <BackupPanel />

      <PwaInstallPanel />

      <p className="text-xs text-muted-foreground">
        ZenTrack browser edition · local-first · no account required
      </p>
    </div>
  );
}
